<template>
  <div class="authoring-copilot-knowledge-base">
    <div class="header">
      <h5 class="title">{{ t("lesson_authoring_copilot_knowledge_base_title") }}</h5>
      <p v-if="headerDescription" class="info">{{ headerDescription }}</p>
      <cv-inline-notification
        v-if="headerInlineNotificationSubtitle"
        kind="info"
        low-contrast
        hide-close-button
      >
        <template #subtitle>
          <div v-safe-html="headerInlineNotificationSubtitle" />
        </template>
      </cv-inline-notification>

      <cv-inline-notification
        v-if="isEditingSpecificActivity"
        kind="info"
        :title="
          t('lesson_authoring_copilot_knowledge_base_specific_activity_inline_notification_title')
        "
        low-contrast
        hide-close-button
      >
        <template #subtitle>
          <i18n-t
            keypath="lesson_authoring_copilot_knowledge_base_specific_activity_inline_notification_subtitle"
          >
            <template #here>
              <cv-link :href="userGuideUrl" target="_blank">
                {{ t("common_here_label") }}
              </cv-link>
            </template>
          </i18n-t>
        </template>
      </cv-inline-notification>
    </div>
    <cv-inline-notification
      v-if="showBannerError"
      kind="error"
      low-contrast
      hide-close-button
      :title="bannerErrorTitle"
      :sub-title="kbValidationResult.bannerMessage"
    >
    </cv-inline-notification>
    <inline-loader v-if="isLoading" />
    <template v-else>
      <div class="knowledge-base-listing">
        <div v-if="showSelectAllCheckbox" class="content-title">
          <selection-bar
            :count="selectedKnowledgeBasesCount"
            @clear="handleClearKnowledgeBaseSelection"
          >
            <template v-if="!isEditingSpecificActivity" #action-tools>
              <selection-bar-menu type="list-menu">
                <selection-bar-menu-item
                  class="delete"
                  icon="Trash24"
                  :label="t('lesson_authoring_copilot_knowledge_base_button_delete')"
                  @click="handleDeleteKnowledgeBases"
                />
              </selection-bar-menu>
            </template>
          </selection-bar>
          <div class="content-input-group">
            <input-checkbox
              id="select-all"
              kind="white"
              value="true"
              :selected-values="[String(selectAllCheckboxState.checked)]"
              :mixed="selectAllCheckboxState.mixed"
              @change="handleSelectAllKnowledgeBases"
            >
              {{ t("lesson_authoring_copilot_knowledge_base_button_select_all") }}
            </input-checkbox>
          </div>
        </div>
        <div class="content-info">
          <AccordionWrapper
            v-for="(item, index) in knowledgeBases"
            :key="item.id"
            :class="{ 'without-checkbox': !isCheckboxSelectionEnabled }"
            @state-change="accordionActionChange(index)"
          >
            <!-- EDIT MODE -->
            <cv-accordion-item
              v-if="isEditMode(item.id)"
              :key="`edit-${item.id}`"
              :open="isAccordionExpanded(index)"
              class="is-edit-mode"
            >
              <template #title>
                <span class="title">{{ accordionTitle }}</span>
                <!-- Delete button for edit mode -->
                <span class="icon">
                  <svg-icon
                    name="Trash24"
                    kind="action"
                    @click.stop.prevent="handleRemoveKnowledgeBase(item, index)"
                  />
                </span>
              </template>
              <template #content>
                <div class="accordion-content">
                  <cv-form class="form-knowledge-base" @submit.prevent>
                    <!-- TEXT type -->
                    <dl
                      v-if="editingKnowledgeBase.contentType === KnowledgeBaseContentType.TEXT"
                      class="field-set type-text knowledge-base-content"
                    >
                      <dd class="field-input">
                        <rich-text-editor
                          ref="editor"
                          size="super_large"
                          kind="teacher_tinymce_6"
                          :subpage-parent-title="subpageParentTitle"
                          :is-mandatory="true"
                          :placeholder="
                            t('lesson_authoring_copilot_knowledge_base_placeholder_content')
                          "
                          :initial-content="editingKnowledgeBase.content"
                          @change="handleKnowledgeBaseContentChange"
                        />
                        <cv-inline-notification
                          v-if="error.content"
                          low-contrast
                          kind="error"
                          hide-close-button
                        >
                          <template #subtitle>
                            <div v-safe-html="error.content" />
                          </template>
                        </cv-inline-notification>
                      </dd>
                    </dl>

                    <!-- MEDIA type -->
                    <dl
                      v-else-if="
                        editingKnowledgeBase.contentType === KnowledgeBaseContentType.MEDIA
                      "
                      class="field-set type-media"
                    >
                      <dd class="field-input">
                        <cv-inline-notification
                          v-if="showPdfKbWarning(editingKnowledgeBase)"
                          kind="warning"
                          :title="t('pdf_capture_kb_pdf_warning_title')"
                          low-contrast
                          hide-close-button
                        >
                          <template #subtitle>
                            <div v-safe-html="t('pdf_capture_kb_pdf_warning_subtitle')" />
                          </template>
                        </cv-inline-notification>
                        <media-component
                          ref="media"
                          :uuid="editingKnowledgeBase.contentId"
                          :mediatype="editingKnowledgeBase.mediaType"
                          type="mo"
                          :show-ai-info-icon="false"
                          display-mode="EDIT"
                          :is-inline="true"
                          :hide-star-button="true"
                          :annotatable="false"
                          :can-mark-copy="false"
                          :lazyload="false"
                          :context="{
                            pageDomain: 'editor',
                            resourceDomain: resourceDomain,
                            ownerGroupId: ownerGroupId,
                          }"
                          @remove="handleMediaDelete"
                          @media-settings="handleMediaSettings"
                          @open-pdf-in-new-tab="handleOpenPdfInNewTab"
                          @unapproved-mo-error-changed="
                            handleMediaUnapprovedErrorChanged(item.id, $event)
                          "
                        />
                      </dd>
                    </dl>

                    <!-- GOOGLE type -->
                    <dl
                      v-else-if="
                        editingKnowledgeBase.contentType === KnowledgeBaseContentType.GOOGLE
                      "
                      class="field-set type-google"
                    >
                      <dd class="field-input">
                        <google-attachment
                          v-if="editingKnowledgeBase.contentId"
                          :id="String(editingKnowledgeBase.contentId)"
                          :is-inline="false"
                          :lazyload="false"
                          :show-button="true"
                          :data-type="'embed'"
                          :data-context="'lesson'"
                          :context="googleAttachmentContext"
                          mode="edit"
                          @remove="handleMediaDelete"
                        />
                      </dd>
                    </dl>

                    <div class="actions">
                      <cv-button
                        class="cancel"
                        kind="tertiary"
                        size="field"
                        @click="handleDiscardEditingKnowledgeBase"
                      >
                        {{ t("button_cancel") }}
                      </cv-button>
                      <fetch-data-button
                        class="btn-save"
                        kind="primary"
                        size="field"
                        :disabled="saveButton.disabled"
                        :loading="saveButton.loading"
                        :error="saveButton.error"
                        :error-message="saveButton.errorMessage"
                        @click="handleSaveKnowledgeBase(index)"
                        @reset-error-state="resetProcessingState"
                      >
                        {{ saveButtonLabel }}
                      </fetch-data-button>
                    </div>
                  </cv-form>
                </div>
              </template>
            </cv-accordion-item>
            <!-- VIEW MODE -->
            <cv-accordion-item
              v-else
              :key="`view-${item.id}-${isAccordionExpanded(index)}`"
              :open="isAccordionExpanded(index)"
            >
              <template #title>
                <span class="title">{{ generateAccordionTitle(item) }}</span>
              </template>
              <template #content>
                <div class="accordion-content">
                  <!-- TEXT type -->
                  <rich-text
                    v-if="item.contentType === KnowledgeBaseContentType.TEXT"
                    :content="item.content"
                    :expandable="false"
                    :expanded="true"
                  />

                  <!-- MEDIA type -->
                  <template v-else-if="item.contentType === KnowledgeBaseContentType.MEDIA">
                    <cv-inline-notification
                      v-if="showPdfKbWarning(item)"
                      kind="warning"
                      :title="t('pdf_capture_kb_pdf_warning_title')"
                      low-contrast
                      hide-close-button
                    >
                      <template #subtitle>
                        <div v-safe-html="t('pdf_capture_kb_pdf_warning_subtitle')" />
                      </template>
                    </cv-inline-notification>
                    <media-component
                      ref="media"
                      :uuid="item.contentId"
                      :mediatype="item.mediaType"
                      type="mo"
                      :show-ai-info-icon="false"
                      display-mode="VIEW"
                      :is-inline="false"
                      :hide-star-button="false"
                      :annotatable="false"
                      :can-mark-copy="false"
                      :lazyload="false"
                      :open-pdf-as-overlay="true"
                      :context="{
                        pageDomain: 'editor',
                        resourceDomain: resourceDomain,
                        ownerGroupId: ownerGroupId,
                      }"
                      @open-pdf-overlay="handleOpenPdfOverlay"
                      @unapproved-mo-error-changed="
                        handleMediaUnapprovedErrorChanged(item.id, $event)
                      "
                    />
                  </template>

                  <!-- GOOGLE type -->
                  <google-attachment
                    v-else-if="item.contentType === KnowledgeBaseContentType.GOOGLE"
                    :id="String(item.contentId)"
                    :is-inline="false"
                    :lazyload="false"
                    :show-button="true"
                    :data-type="'embed'"
                    :data-context="'lesson'"
                    :context="googleAttachmentContext"
                    mode="view"
                  />

                  <!-- Per-file inline error -->
                  <message-component v-if="getFileError(item.id)" class="error">
                    {{ getFileError(item.id)?.message }}
                  </message-component>
                </div>
              </template>
            </cv-accordion-item>

            <!-- Checkbox for Knowledge base title row-->
            <input-checkbox
              v-if="isCheckboxSelectionEnabled"
              :id="String(item.id)"
              value="true"
              :selected-values="[String(isSelected(item.id))]"
              @click.stop.prevent
              @click="handleSelectKnowledgeBase(item)"
            />

            <!-- Kebab overflow menu for Knowledge base title row-->
            <overflow-menu
              v-if="!isEditMode(item.id) && !isEditingSpecificActivity"
              flip-menu
              class="menu-actions"
            >
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <OverflowMenuItem @click="handleEditKnowledgeBase(index)">
                <svg-icon name="Pencil24" kind="action" />
                {{ t("lesson_authoring_copilot_knowledge_base_button_edit") }}
              </OverflowMenuItem>
              <OverflowMenuItem
                v-if="!isFirstIndexKnowledgeBase(index)"
                @click="handleMoveUp(index)"
              >
                <svg-icon name="ArrowUp24" kind="action" />
                {{ t("lesson_authoring_copilot_knowledge_base_button_move_up") }}
              </OverflowMenuItem>
              <OverflowMenuItem
                v-if="!isLastIndexKnowledgeBase(index)"
                @click="handleMoveDown(index)"
              >
                <svg-icon name="ArrowDown24" kind="action" />
                {{ t("lesson_authoring_copilot_knowledge_base_button_move_down") }}
              </OverflowMenuItem>
              <OverflowMenuItem @click="handleDeleteKnowledgeBase(item.id)">
                <svg-icon name="Trash24" kind="action" />
                {{ t("lesson_authoring_copilot_knowledge_base_button_delete") }}
              </OverflowMenuItem>
            </overflow-menu>
            <message-component
              v-if="
                getFileError(item.id) ||
                (kbValidationResult.valid && getMediaUnapprovedError(item.id))
              "
              class="error"
            >
              {{ t("kb_validation_error_accordion") }}
            </message-component>
          </AccordionWrapper>
          <dl class="field-set type-button add-knowledge-base">
            <dd class="field-input action-button-wrapper">
              <div class="add-kb-button-wrapper">
                <overflow-menu-button
                  class="btn-add-knowledge-base"
                  type="button"
                  kind="tertiary"
                  size="field"
                  :disabled="isAddKnowledgeBaseButtonDisabled"
                  :tip="addKnowledgeBaseButtonTip"
                >
                  <span>{{ t("lesson_authoring_copilot_knowledge_base_button_add") }}</span>
                  <template #overflow-menu>
                    <OverflowMenuItem @click="handleAddTextKnowledgeBase">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_text") }}
                    </OverflowMenuItem>
                    <OverflowMenuItem @click="handleMediaFromLibrary">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_media_library") }}
                    </OverflowMenuItem>
                    <OverflowMenuItem @click="handleMediaFromMyDrive">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_media_my_drive") }}
                    </OverflowMenuItem>
                    <OverflowMenuItem @click="handleMediaFromStarred">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_media_starred") }}
                    </OverflowMenuItem>
                    <OverflowMenuItem @click="handleMediaFromGoogle">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_media_google") }}
                    </OverflowMenuItem>
                    <OverflowMenuItem @click="handleMediaUpload">
                      {{ t("lesson_authoring_copilot_knowledge_base_dropdown_media_upload") }}
                    </OverflowMenuItem>
                  </template>
                </overflow-menu-button>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import RichTextContextMixin from "@modules/editor/mixin/richtext.context.mixin";
import AuthoringCoPilotApi from "@apis/authoring.copilot.api";
import { isEmpty } from "@utils/var.check";
import AuthoringCoPilotKnowledgeBaseDeleteModal from "@modules/editor/components/authoring-copilot/AuthoringCoPilotKnowledgeBaseDeleteModal.vue";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import {
  ERROR_CODE,
  ADD_COMPONENT_TYPE,
  ADD_FROM_LIBRARY_SEARCH_MODE,
  ADD_FROM_EXISTING_TYPE,
  ADD_FROM_EXISTING_LOCATION,
  MEDIA_TYPE,
} from "@modules/editor/service/editor.constant";
import {
  MAX_KB_TITLE_LENGTH,
  type KbValidationResult,
  LEA_SPEECH_RELATED_TYPES,
} from "@modules/editor/service/authoring.copilot.constant.ts";
import { KnowledgeBaseContentType } from "@plugins/authoring-copilot/types.ts";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import MediaSettingsSubpage from "@plugins/media/components/MediaSettingsSubpage.vue";
import GoogleAttachment from "@plugins/google/components/GoogleAttachment.vue";
import GooglePicker from "@plugins/google/google.picker";
import { PICKER_ERROR, CONFIG } from "@plugins/google/google.constant";
import GoogleAttachmentService from "@plugins/richtext/service/google.attachment.service";
import AddFromStarredResourcesContentSubpage from "@modules/editor/components/lesson/AddFromStarredResourcesContentSubpage.vue";
import FileUploaderModal from "@plugins/file-uploader-v2/components/FileUploaderModal.vue";
import MediaObjectService from "@plugins/richtext/service/media.object.service";
import { FileResponse, FileStatus, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import {
  ref,
  computed,
  watch,
  onBeforeUnmount,
  onMounted,
  inject,
  provide,
  useTemplateRef,
  nextTick,
} from "vue";
import mediaEventBus from "@plugins/media/service/event-bus";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useGoogleCommon } from "@plugins/google/composables/google-common.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import AddMediaObjectContentSubpage from "@modules/resource-search/AddMediaObjectContentSubpage.vue";
import { RESOURCE_DOMAIN } from "@plugins/common/constant/resource.constant.ts";
import { AddMediaObjectFromLibraryContentSubpageSelectionEvent } from "@modules/resource-search/resource-search-types.ts";
import randomUtils from "@utils/random.utils.ts";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import { useRouter } from "vue-router";
import { usePdfCaptureOverlay } from "@plugins/authoring-copilot/use-pdf-capture-overlay.ts";
import { PDF_VIEWER_OPEN_KEY } from "@plugins/pdf-viewer/composables/use-pdf-viewer-open.ts";
import mediaObjectApi from "@apis/media.object.api";
import type { RichTextContext } from "@plugins/richtext/types.ts";
import type { LearningAssistantType } from "@modules/editor/components/topic/types.ts";
import { useEditorWorkflow } from "@/modules/editor/composables/editor-workflow";

interface GooglePickerEvent {
  action: string;
  docs: {
    id: string;
    mimeType: string;
    name: string;
  }[];
}

interface StarredMediaSelection {
  uuid: string;
  title: string;
  type?: string;
  fileType?: string;
}

interface KnowledgeBaseMediaSelection {
  uuid: string;
  title: string;
  mediaType: string;
}

interface Props {
  headerDescription?: string;
  resourceType: string;
  resourceId: number;
  isCheckboxSelectionEnabled?: boolean;
  showExceedFilesError?: boolean;
  isAllKnowledgeBaseSelected?: boolean;
  selectedKnowledgeBaseIds?: unknown[];
  subpageParentTitle?: unknown[];
  lessonTitle?: string | null;
  isFromLea?: boolean;
  learningAssistantType?: LearningAssistantType | "";
  featureType?: string | null;
  kbValidationResult?: KbValidationResult;
}

// Props
const props = withDefaults(defineProps<Props>(), {
  headerDescription: "",
  isCheckboxSelectionEnabled: true,
  showExceedFilesError: false,
  isAllKnowledgeBaseSelected: false,
  selectedKnowledgeBaseIds: () => [],
  subpageParentTitle: () => [],
  lessonTitle: null,
  isFromLea: false,
  learningAssistantType: "",
  featureType: null,
  kbValidationResult: () => ({ valid: true, errorType: null, fileErrors: [] }),
});

const emit = defineEmits(["knowledge-base-selection", "child-dirty-changed"]);
defineOptions({
  mixins: [RichTextContextMixin],
});

// Data
const editorRef = useTemplateRef("editor");
const isLoading = ref(true);
const knowledgeBases = ref([]);
const selectedKnowledgeBases = ref([]);
const expandedKnowledgeBaseIndices = ref<Set<number>>(new Set());
const editingKnowledgeBase = ref(null); // Knowledge base that is currently being edited
const maxKnowledgeBases = ref(20); // Maximum of knowledge base entries can be added
const saveButton = ref({
  disabled: false,
  loading: false,
  error: false,
  errorMessage: "",
});
const error = ref({
  content: "",
});
const dirtyId = ref(`kb-${props.resourceId}`);
const isUploadingGoogle = ref(false);
const modalId = ref(null);
const mediaUnapprovedErrorMap = ref(new Map<number, boolean>());
const userGuideUrl = ref(""); // TODO: [SLS-12288] Update once url is ready

const { t } = useI18n();
const modal = useModal();
const contentSubpage = useContentSubpageInstance();
const { setDirty, removeComponentDirty, promptComponentDirty } = useDirty(dirtyId);
const { openNotLinkedModal, checkUserEditPermission, openGrantEditPermissionModal } =
  useGoogleCommon();
const { passiveApiFailHandler } = useErrorHandler();
const router = useRouter();
const { open: openPdfCaptureOverlay } = usePdfCaptureOverlay();
const { isEditingSpecificActivity } = useEditorWorkflow();

const richTextContext = inject<RichTextContext>("richTextContext");

// Computed
const ownerGroupId = computed(() => richTextContext?.ownerGroupId);
const resourceDomain = computed(() => richTextContext?.resourceDomain);

const googleAttachmentContext = computed(() => ({
  lessonId: props.resourceId,
  ...(richTextContext || {}),
}));

const isEditMode = computed(() => {
  return (id) => {
    return editingKnowledgeBase.value?.id === id;
  };
});

const isAccordionExpanded = computed(() => {
  return (index) => {
    return expandedKnowledgeBaseIndices.value.has(index);
  };
});

const isMaxKnowledgeBaseAdded = computed(() => {
  return knowledgeBases.value.length >= maxKnowledgeBases.value;
});

const isAddKnowledgeBaseButtonDisabled = computed(() => {
  return editingKnowledgeBase.value || isMaxKnowledgeBaseAdded.value;
});

const addKnowledgeBaseButtonTip = computed(() => {
  return isMaxKnowledgeBaseAdded.value
    ? t("lesson_authoring_copilot_knowledge_base_button_add_tooltip_max_record_reached", {
        number: maxKnowledgeBases.value,
      })
    : null;
});

const selectedKnowledgeBasesCount = computed(() => {
  return selectedKnowledgeBases.value.length;
});

const showSelectAllCheckbox = computed(() => {
  return props.isCheckboxSelectionEnabled && knowledgeBases.value.length > 0;
});

const selectAllCheckboxState = computed(() => {
  return {
    checked:
      selectedKnowledgeBasesCount.value > 0 &&
      selectedKnowledgeBasesCount.value === knowledgeBases.value.length,
    mixed:
      selectedKnowledgeBasesCount.value > 0 &&
      selectedKnowledgeBasesCount.value !== knowledgeBases.value.length,
  };
});

const headerInlineNotificationSubtitle = computed(() => {
  if (!props.isCheckboxSelectionEnabled && knowledgeBases.value.length > 0) {
    return t("lesson_authoring_copilot_knowledge_base_inline_notification_subtitle");
  }

  return "";
});

const accordionTitle = computed(() => {
  if (!editingKnowledgeBase.value) {
    return "-";
  }
  return generateAccordionTitle(editingKnowledgeBase.value);
});

const saveButtonLabel = computed(() => {
  return saveButton.value.error ? t("button_state_error") : t("button_save");
});

// Computed helpers for KB validation result

const getFileError = computed(() => (kbId: number) => {
  return props.kbValidationResult.fileErrors.find((e) => e.knowledgeBaseId === kbId);
});

const getMediaUnapprovedError = computed(() => (kbId: number): boolean => {
  return mediaUnapprovedErrorMap.value.get(kbId) ?? false;
});

const showBannerError = computed(() => !!props.kbValidationResult.bannerMessage);

const bannerErrorTitle = computed(() => {
  if (props.kbValidationResult.errorType === "MAX_FILES") {
    return t("kb_validation_exceed_max_files_title");
  }
  if (props.kbValidationResult.errorType === "FILE_SIZE") {
    return t("kb_validation_exceed_file_size_title");
  }
  return "";
});

const showPdfKbWarning = computed(() => {
  return (item) =>
    item.mediaType === MEDIA_TYPE.PDF &&
    LEA_SPEECH_RELATED_TYPES.includes(props.learningAssistantType);
});

// Methods
const accordionActionChange = (index: number) => {
  if (expandedKnowledgeBaseIndices.value.has(index)) {
    expandedKnowledgeBaseIndices.value.delete(index);
  } else {
    expandedKnowledgeBaseIndices.value.add(index);
  }
  // Trigger reactivity (Set mutations are not reactive by default)
  expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
};

const getSelectedKnowledgeBaseIds = (includeEmptyId) => {
  return selectedKnowledgeBases.value
    .map((kb) => kb.id)
    .filter((id) => includeEmptyId || !isEmpty(id));
};

const isFirstIndexKnowledgeBase = (index) => {
  return index === 0;
};

const isLastIndexKnowledgeBase = (index) => {
  return index === knowledgeBases.value.filter((kb) => !isEmpty(kb.id)).length - 1;
};

const generateAccordionTitle = (kb): string => {
  if (!kb) return "-";

  switch (kb.contentType) {
    case KnowledgeBaseContentType.TEXT: {
      const text = richTextUtils.removeMediaAndStripHTML(String(kb.content || ""));
      const firstLine = (richTextUtils.splitTextByNewLine(text.trimStart())[0] || "").trimEnd();
      return firstLine.substring(0, MAX_KB_TITLE_LENGTH) || "-";
    }
    case KnowledgeBaseContentType.MEDIA:
    case KnowledgeBaseContentType.GOOGLE:
      return kb.mediaTitle || "-";

    default:
      return "-";
  }
};

const fetchKnowledgeBases = async () => {
  try {
    const response = await AuthoringCoPilotApi.getKnowledgeBases({
      resourceType: props.resourceType,
      resourceId: props.resourceId,
    });

    if (response.code !== "ok") {
      throw response.errorMap;
    }

    knowledgeBases.value = response.data || [];
    initSelection();
  } catch (error) {
    passiveApiFailHandler(error);
    knowledgeBases.value = [];
    initSelection();
    return;
  }
};

const extractContentId = (payload) => {
  if (!payload) return null;
  if (typeof payload === "object") {
    return payload.id ?? payload.contentId ?? null;
  }
  return payload;
};

const handleMediaDelete = async (raw) => {
  const contentId = extractContentId(raw);
  if (!contentId) return;
  const matchIndex = knowledgeBases.value.findIndex((kb) => kb.contentId === contentId);
  if (matchIndex === -1) return;
  const kb = knowledgeBases.value[matchIndex];

  deleteKnowledgeBases([kb.id], {
    title: t("lesson_authoring_copilot_knowledge_base_delete_media_title"),
    message: t("lesson_authoring_copilot_knowledge_base_delete_media_message"),
  });
};

const handleMediaSettings = (settings: Record<string, unknown>) => {
  contentSubpage.show(MediaSettingsSubpage, settings, {
    updateMediaObject: handleUpdateMediaObject,
  });
};

const setMediaMetadata = (uuid: string, title?: string, mediaType?: string) => {
  const updates: Record<string, unknown> = {};
  if (title !== undefined && title !== null) updates.mediaTitle = title;
  if (mediaType !== undefined && mediaType !== null) updates.mediaType = mediaType;
  if (!Object.keys(updates).length) return;

  // Always update the knowledgeBases list entry so the accordion title stays in sync
  const kb = knowledgeBases.value.find((k) => k?.contentId === uuid);
  if (kb) {
    Object.assign(kb, updates);
  }

  // Also update the editing copy if one exists (keeps in-edit form consistent)
  if (editingKnowledgeBase.value?.contentId === uuid) {
    Object.assign(editingKnowledgeBase.value, updates);
  }
};

// Handler invoked when a media object is updated (from mixin or subpage)
const handleUpdateMediaObject = (mediaObject) => {
  if (!mediaObject || !mediaObject.uuid) {
    return;
  }
  setMediaMetadata(mediaObject.uuid, mediaObject.title, mediaObject.mediaType);
};

const handleSelectAllKnowledgeBases = () => {
  if (selectAllCheckboxState.value.checked) {
    // If all selected, clear all
    handleClearKnowledgeBaseSelection();
  } else {
    // If some or none is selected, add all regardless
    selectedKnowledgeBases.value = [...knowledgeBases.value];
  }
};

const handleSelectKnowledgeBase = (knowledgeBase) => {
  if (isSelected(knowledgeBase.id)) {
    // If selected, deselect it
    selectedKnowledgeBases.value = selectedKnowledgeBases.value.filter(
      (kb) => kb.id !== knowledgeBase.id,
    );
  } else {
    // If not selected, add it to the selected list
    selectedKnowledgeBases.value.push(knowledgeBase);
  }
};

const isSelected = (knowledgeBaseId) => {
  return !!selectedKnowledgeBases.value.find((kb) => kb.id === knowledgeBaseId);
};

const handleSetDirty = () => {
  error.value.content = "";
  setDirty(dirtyId.value);
  emit("child-dirty-changed", true);
};

const handleRemoveDirty = () => {
  error.value.content = "";
  removeComponentDirty();
  emit("child-dirty-changed", false);
};

const handleClearKnowledgeBaseSelection = () => {
  selectedKnowledgeBases.value = [];
};

const handleKnowledgeBaseContentChange = (value) => {
  handleSetDirty();
  editingKnowledgeBase.value.content = value;
};

const handleAddTextKnowledgeBase = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  resetProcessingState();
  handleSetDirty();
  editingKnowledgeBase.value = {
    id: null,
    contentType: KnowledgeBaseContentType.TEXT,
    content: "",
    indexNo: knowledgeBases.value.length,
  };
  expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
  expandedKnowledgeBaseIndices.value.add(editingKnowledgeBase.value.indexNo);
  knowledgeBases.value.push(editingKnowledgeBase.value);
  handleSelectKnowledgeBase({ ...editingKnowledgeBase.value });
};

const handleMediaFromLibrary = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  const contentSubpageParams = {
    baseTitle: [
      richTextContext?.lessonTitle || props.lessonTitle,
      props.subpageParentTitle[0],
      t("add_from_library_label"),
    ],
    restrictMediaType: [] as string[],
    restrictLocation: [RESOURCE_DOMAIN.COMMUNITY, RESOURCE_DOMAIN.MOE],
    showKeywordSearchFilterAccordion: true,
    checkCopyRightForMOEResource: false,
    fromMoeLibrary: richTextContext?.resourceDomain === "MOE",
  };

  contentSubpage.show(AddMediaObjectContentSubpage, contentSubpageParams, {
    selected: handleMediaSelected,
  });
};

const handleMediaFromMyDrive = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  const contentSubpageParams = {
    baseTitle: [
      richTextContext?.lessonTitle || props.lessonTitle,
      props.subpageParentTitle[0],
      t("add_from_my_drive_label"),
    ],
    restrictMediaType: [] as string[],
    restrictLocation: [RESOURCE_DOMAIN.PERSONAL],
    showKeywordSearchFilterAccordion: true,
    checkCopyRightForMOEResource: false,
    fromMoeLibrary: false,
  };

  contentSubpage.show(AddMediaObjectContentSubpage, contentSubpageParams, {
    selected: handleMediaSelected,
  });
};

const handleMediaFromStarred = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  const mediaTypes = [
    MEDIA_TYPE.IMAGE,
    MEDIA_TYPE.AUDIO,
    MEDIA_TYPE.VIDEO,
    MEDIA_TYPE.HTML5,
    MEDIA_TYPE.POWERPOINT,
    MEDIA_TYPE.WORD,
    MEDIA_TYPE.EXCEL,
    MEDIA_TYPE.PDF,
    MEDIA_TYPE.OTHERS,
    MEDIA_TYPE.ZIP,
  ];

  contentSubpage.show(
    AddFromStarredResourcesContentSubpage,
    {
      resourceType: ADD_COMPONENT_TYPE.MEDIA,
      mediaTypes: mediaTypes,
      copyRightForMOEResource: false,
      mode: ADD_FROM_LIBRARY_SEARCH_MODE.SINGLE_SELECTION,
      addFromType: ADD_FROM_EXISTING_TYPE.MEDIA,
      addFromLocation: ADD_FROM_EXISTING_LOCATION.STARRED_RESOURCES,
      allowUpload: () => true, // Required for MEDIA type - mixin checks this before calling handleSaveMediaObject
      subpageParentTitle: props.subpageParentTitle,
    },
    {
      onMediaSelected: handleStarredMediaSelected,
    },
  );
};

const handleMediaFromGoogle = async () => {
  if (isUploadingGoogle.value) return; // prevent re-entry while uploading
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  const options = {
    pickerCallback: handleGoogleDocPicked,
  };

  const googlePicker = new GooglePicker(options);
  googlePicker.loadPicker(CONFIG.EMBED).then(
    () => {
      // Picker loaded successfully
    },
    (error) => {
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        openNotLinkedModal("openInNewTab");
      }
    },
  );
};

const handleMediaUpload = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();
  if (!canProceed) {
    return;
  }

  modalId.value = modal.show(
    FileUploaderModal,
    {
      uploadFlow: UploadFlow.ANY_FILE,
      modalId: randomUtils.randomId(),
    },
    {
      "update:fileEntry": handleFileUploaded,
    },
  );
};

/**
 * Called by PdfViewerCaptureOverlay when a capture has been saved successfully.
 * The KB entry was already persisted by the overlay's PdfCaptureService, so we
 * just append it to the local list and mark it selected.
 */
const handlePdfCaptureSaved = (kbEntry: unknown) => {
  if (!kbEntry || typeof kbEntry !== "object") return;
  const entry = kbEntry as Record<string, unknown>;

  const newKnowledgeBase = {
    id: entry.id ?? null,
    contentType: KnowledgeBaseContentType.MEDIA,
    contentId: entry.contentId ?? null,
    mediaTitle: entry.mediaTitle ?? null,
    mediaType: entry.mediaType ?? "IMAGE",
    content: "",
    indexNo: knowledgeBases.value.length,
    mimeType: entry.mimeType,
  };

  knowledgeBases.value.push(newKnowledgeBase);
  handleSelectKnowledgeBase({ ...newKnowledgeBase });
};

/**
 * Override the global PDF viewer open handler so that any PDF MediaComponent
 * rendered inside this KB listing opens with KB capture context enabled.
 * All other MediaComponents (outside this component) get the default view-only handler
 * provided by pdfViewerPlugin at app level.
 */
provide(
  PDF_VIEWER_OPEN_KEY,
  async (payload: {
    uuid: string;
    filename?: string;
    contextType?: string;
    contextUuid?: string;
  }) => {
    if (!payload?.uuid) return;

    try {
      const viewPdfParams: Record<string, string> = { uuid: payload.uuid };
      if (payload.contextUuid) viewPdfParams.contextUuid = payload.contextUuid;
      if (payload.contextType) viewPdfParams.contextType = payload.contextType;

      const response = await mediaObjectApi.viewPdf(viewPdfParams);
      if (!response?.data) {
        passiveApiFailHandler(new Error("Failed to load PDF URL"));
        return;
      }

      const matchingKb = knowledgeBases.value.find((kb) => kb.contentId === payload.uuid);
      const filename = String(matchingKb?.mediaTitle ?? payload.filename ?? payload.uuid);

      openPdfCaptureOverlay({
        pdfUrl: String(response.data),
        pdfFilename: filename,
        mediaObjectUuid: payload.uuid,
        resourceType: props.resourceType,
        resourceId: props.resourceId,
        currentKbCount: knowledgeBases.value.length,
        maxKbAllowed: maxKnowledgeBases.value,
        richTextContext: richTextContext ?? null,
        onImageSaved: handlePdfCaptureSaved,
      });
    } catch (err) {
      passiveApiFailHandler(err);
    }
  },
);

const handleFileUploaded = async (fileResponse: FileResponse) => {
  if (!fileResponse || fileResponse.status !== FileStatus.READY || !fileResponse.uuid) {
    return;
  }

  try {
    const mediaObjectService = new MediaObjectService(richTextContext);

    const mediaObjectData = await mediaObjectService.create(fileResponse);

    if (!mediaObjectData?.uuid) {
      passiveApiFailHandler(mediaObjectData);
      return;
    }

    if (modalId.value) {
      modal.remove(modalId.value);
    }

    resetProcessingState();
    handleSetDirty();

    const rawTitle = mediaObjectData.title || fileResponse.name || "";
    // Strip file extension to match the sanitized title stored by the backend
    const mediaTitle = rawTitle.replace(/\.[^/.]+$/, "") || rawTitle;

    const newKnowledgeBase = {
      id: null,
      contentType: KnowledgeBaseContentType.MEDIA,
      contentId: mediaObjectData.uuid,
      mediaTitle,
      mediaType: mediaObjectData.fileType,
      content: "",
      indexNo: knowledgeBases.value.length,
    };

    editingKnowledgeBase.value = newKnowledgeBase;

    await nextTick();

    expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
    expandedKnowledgeBaseIndices.value.add(newKnowledgeBase.indexNo);
    knowledgeBases.value.push(newKnowledgeBase);
    handleSelectKnowledgeBase({ ...newKnowledgeBase });
  } catch (err) {
    passiveApiFailHandler(err);
  }
};

const handleMediaSelected = async (
  mediaObjectSelection: AddMediaObjectFromLibraryContentSubpageSelectionEvent,
) => {
  if (!mediaObjectSelection || !mediaObjectSelection.data) {
    mediaObjectSelection?.onFailure?.();
    return;
  }

  const mediaResultItem = mediaObjectSelection.data;
  try {
    await createMediaKnowledgeBase({
      uuid: mediaResultItem.uuid,
      title: mediaResultItem.title,
      mediaType: mediaResultItem.objectType,
    });
    mediaObjectSelection.onSuccess();
  } catch (err) {
    mediaObjectSelection.onFailure();
    passiveApiFailHandler(err);
  }
};

const handleStarredMediaSelected = async (
  mediaSelections: StarredMediaSelection[],
): Promise<void> => {
  const [mediaSelection] = mediaSelections;
  const mediaType = mediaSelection?.type ?? mediaSelection?.fileType;

  if (!mediaSelection || !mediaType) {
    return;
  }

  await createMediaKnowledgeBase({
    uuid: mediaSelection.uuid,
    title: mediaSelection.title,
    mediaType,
  });
};

const createMediaKnowledgeBase = async (
  mediaSelection: KnowledgeBaseMediaSelection,
): Promise<void> => {
  resetProcessingState();
  handleSetDirty();

  // Create KB entry with explicit field assignment
  const newKnowledgeBase = {
    id: null,
    contentType: KnowledgeBaseContentType.MEDIA,
    contentId: mediaSelection.uuid,
    mediaTitle: mediaSelection.title,
    mediaType: mediaSelection.mediaType,
    content: "",
    indexNo: knowledgeBases.value.length,
  };

  editingKnowledgeBase.value = newKnowledgeBase;

  // Use nextTick to ensure Vue reactivity processes the update before rendering
  await nextTick();

  expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
  expandedKnowledgeBaseIndices.value.add(newKnowledgeBase.indexNo);
  knowledgeBases.value.push(newKnowledgeBase);
  handleSelectKnowledgeBase({ ...newKnowledgeBase });
};

const handleGoogleDocPicked = async (event: GooglePickerEvent) => {
  if (!window.google?.picker || event.action !== window.google.picker.Action.PICKED) return;

  const googleDoc = event.docs[0];

  const [canEdit, mimeType] = await checkUserEditPermission(googleDoc.id);
  if (!canEdit) {
    openGrantEditPermissionModal(googleDoc.id, mimeType);
    return;
  }

  const attachmentService = new GoogleAttachmentService({ lessonId: props.resourceId });
  let slsDocument;
  if (isUploadingGoogle.value) return;
  isUploadingGoogle.value = true;
  try {
    slsDocument = await attachmentService.create(
      {
        documentId: googleDoc.id,
        mimeType: googleDoc.mimeType,
        title: googleDoc.name,
      },
      true,
    );
  } catch (e) {
    passiveApiFailHandler(e);
    return;
  } finally {
    isUploadingGoogle.value = false;
  }

  resetProcessingState();
  handleSetDirty();

  editingKnowledgeBase.value = {
    id: null,
    contentType: KnowledgeBaseContentType.GOOGLE,
    contentId: slsDocument.id,
    mediaTitle: googleDoc.name,
    content: "",
    indexNo: knowledgeBases.value.length,
  };

  expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
  expandedKnowledgeBaseIndices.value.add(editingKnowledgeBase.value.indexNo);
  knowledgeBases.value.push(editingKnowledgeBase.value);
  handleSelectKnowledgeBase({ ...editingKnowledgeBase.value });
};

const shouldSkipApiUpdate = (kb) => {
  return !isEmpty(kb.id) && kb.contentType !== KnowledgeBaseContentType.TEXT;
};

const confirmSaveKnowledgeBaseForSpecificActivity = async (): Promise<boolean> => {
  if (!isEditingSpecificActivity.value) {
    return true;
  }

  return new Promise((resolve) => {
    modal.showMessage("warning", {
      title: t("lesson_authoring_copilot_knowledge_base_specific_activity_save_modal_title"),
      content: t("lesson_authoring_copilot_knowledge_base_specific_activity_save_modal_content"),
      primaryBtnLabel: t(
        "lesson_authoring_copilot_knowledge_base_specific_activity_save_modal_confirm_label",
      ),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        resolve(true);
      },
      actionSecondary: () => {
        resolve(false);
      },
      actionHideRequest: () => {
        resolve(false);
      },
    });
  });
};

const applySavedKnowledgeBase = (
  kbResponse,
  wasSelected: boolean,
  index: number,
  isNewEntry: boolean,
) => {
  saveButton.value.disabled = false;
  saveButton.value.loading = false;

  knowledgeBases.value[index] = kbResponse;

  if (wasSelected) {
    if (isNewEntry) {
      selectedKnowledgeBases.value = selectedKnowledgeBases.value.filter(
        (kb) =>
          kb.id !== editingKnowledgeBase.value.id &&
          !(isEmpty(kb.id) && kb.indexNo === editingKnowledgeBase.value.indexNo),
      );
      selectedKnowledgeBases.value.push(kbResponse);
    } else {
      const selectedIndex = selectedKnowledgeBases.value.findIndex((kb) => kb.id === kbResponse.id);
      if (selectedIndex !== -1) {
        selectedKnowledgeBases.value.splice(selectedIndex, 1, kbResponse);
      }
    }
  } else if (!isNewEntry && !isSelected(kbResponse.id)) {
    handleSelectKnowledgeBase(kbResponse);
  }

  editingKnowledgeBase.value = null;
  handleRemoveDirty();
};

const handleSaveKnowledgeBase = async (index) => {
  if (!validate()) {
    return;
  }

  const { id } = editingKnowledgeBase.value;
  const wasSelected = isSelected(id);
  const isNewEntry = isEmpty(id);

  const shouldProceed = await confirmSaveKnowledgeBaseForSpecificActivity();
  if (!shouldProceed) {
    return;
  }

  // skip update for kb with non TEXT contentType
  if (shouldSkipApiUpdate(editingKnowledgeBase.value)) {
    applySavedKnowledgeBase({ ...editingKnowledgeBase.value }, wasSelected, index, isNewEntry);
    return;
  }

  saveButton.value.disabled = true;
  saveButton.value.loading = true;

  const requestData = populateKnowledgeBaseRequest();
  const response = isNewEntry
    ? await AuthoringCoPilotApi.createKnowledgeBase(requestData)
    : await AuthoringCoPilotApi.updateKnowledgeBase(requestData);

  if (response.code !== "ok") {
    setProcessingError();

    if (response.errorMap?.[0] === ERROR_CODE.EXCEED_MAX_NUMBER_OF_KNOWLEDGE_BASES_PER_RESOURCE) {
      saveButton.value.errorMessage = t(
        isEditingSpecificActivity.value
          ? "lesson_authoring_copilot_knowledge_base_specific_activity_max_record_reached"
          : "lesson_authoring_copilot_knowledge_base_button_add_tooltip_max_record_reached",
        { number: maxKnowledgeBases.value },
      );
    }
    return;
  }

  const kbResponse = {
    ...(response.data ?? {}),
    contentId: response.data?.contentId ?? editingKnowledgeBase.value?.contentId ?? null,
    mediaTitle: editingKnowledgeBase.value?.mediaTitle ?? null,
    mediaType: editingKnowledgeBase.value?.mediaType ?? null,
  };

  applySavedKnowledgeBase(kbResponse, wasSelected, index, isNewEntry);
};

const handleDiscardEditingKnowledgeBase = async () => {
  const confirmDiscard = await promptComponentDirty();

  if (!confirmDiscard) {
    return false; // Cancel to discard
  }

  if (!editingKnowledgeBase.value) {
    return true; // Nothing to discard
  }

  if (isEmpty(editingKnowledgeBase.value.id)) {
    // Discard new knowledge base that hasn't been saved.
    const editingIndex = knowledgeBases.value.findIndex(
      (kb) =>
        kb === editingKnowledgeBase.value || kb.indexNo === editingKnowledgeBase.value.indexNo,
    );
    if (editingIndex !== -1) {
      expandedKnowledgeBaseIndices.value.delete(editingIndex);
      expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
      knowledgeBases.value.splice(editingIndex, 1);
    }
    selectedKnowledgeBases.value = selectedKnowledgeBases.value.filter((kb) => !isEmpty(kb.id));
  }
  editingKnowledgeBase.value = null;
  handleRemoveDirty();

  return true;
};

const handleEditKnowledgeBase = async (index) => {
  const isDiscarded = await handleDiscardEditingKnowledgeBase();

  if (!isDiscarded) {
    return; // User cancels to discard the change
  }

  // Proceed with editing
  resetProcessingState();
  expandedKnowledgeBaseIndices.value = new Set(expandedKnowledgeBaseIndices.value);
  expandedKnowledgeBaseIndices.value.add(index);
  editingKnowledgeBase.value = { ...knowledgeBases.value[index] };
};

const reorderKnowledgeBase = async (requestData) => {
  try {
    const response = await AuthoringCoPilotApi.reorderKnowledgeBase(requestData);
    if (response.code !== "ok") {
      throw response.errorMap;
    }
  } catch (error) {
    throw error;
  }
};

const handleMoveUp = async (index) => {
  if (index === 0) return; // Prevent moving up the first item

  const canProceed = await handleDiscardEditingKnowledgeBase();

  if (!canProceed) {
    return;
  }

  const originalKnowledgeBases = knowledgeBases.value;
  const sourceKnowledgeBase = originalKnowledgeBases[index];
  const targetKnowledgeBase = originalKnowledgeBases[index - 1];

  const requestData = {
    knowledgeBaseId: targetKnowledgeBase.id,
    knowledgeBaseIdToSwap: sourceKnowledgeBase.id,
  };

  // Call reorderKnowledgeBase and wait for the promise to resolve
  try {
    await reorderKnowledgeBase(requestData);

    // Move the knowledge base up by swapping with the one above it
    knowledgeBases.value.splice(index - 1, 2, sourceKnowledgeBase, targetKnowledgeBase);

    // Shift expanded indices: index moves to index-1, index-1 moves to index
    const newIndices = new Set<number>();
    for (const i of expandedKnowledgeBaseIndices.value) {
      if (i === index) newIndices.add(index - 1);
      else if (i === index - 1) newIndices.add(index);
      else newIndices.add(i);
    }
    expandedKnowledgeBaseIndices.value = newIndices;
  } catch (error) {
    throw error;
  }
};

const handleMoveDown = async (index) => {
  if (index === knowledgeBases.value.length - 1) return; // Prevent moving down the last item

  const canProceed = await handleDiscardEditingKnowledgeBase();

  if (!canProceed) {
    return;
  }

  const originalKnowledgeBases = knowledgeBases.value;
  const sourceKnowledgeBase = originalKnowledgeBases[index];
  const targetKnowledgeBase = originalKnowledgeBases[index + 1];

  const requestData = {
    knowledgeBaseId: sourceKnowledgeBase.id,
    knowledgeBaseIdToSwap: targetKnowledgeBase.id,
  };

  // Call reorderKnowledgeBase and wait for the promise to resolve
  try {
    await reorderKnowledgeBase(requestData);

    // Move the knowledge base down by swapping with the one below it
    knowledgeBases.value.splice(index, 2, targetKnowledgeBase, sourceKnowledgeBase);

    // Shift expanded indices: index moves to index+1, index+1 moves to index
    const newIndices = new Set<number>();
    for (const i of expandedKnowledgeBaseIndices.value) {
      if (i === index) newIndices.add(index + 1);
      else if (i === index + 1) newIndices.add(index);
      else newIndices.add(i);
    }
    expandedKnowledgeBaseIndices.value = newIndices;
  } catch (error) {
    throw error;
  }
};

const handleRemoveKnowledgeBase = (item, index) => {
  if (isEmpty(item.id)) {
    handleDeleteNewKnowledgeBase(item, index);
  } else {
    handleDeleteKnowledgeBase(item.id);
  }
};

const handleDeleteNewKnowledgeBase = (item, index = null) => {
  const removeIndex =
    index ?? knowledgeBases.value.findIndex((kb) => kb === item || kb.indexNo === item.indexNo);

  modal.show(AuthoringCoPilotKnowledgeBaseDeleteModal, {
    ids: [],
    actionPrimary: () => {
      if (editingKnowledgeBase.value && removeIndex !== -1) {
        editingKnowledgeBase.value = null;
        handleRemoveDirty();
      }

      if (removeIndex !== -1) {
        knowledgeBases.value.splice(removeIndex, 1);
      }

      selectedKnowledgeBases.value = selectedKnowledgeBases.value.filter((kb) => !isEmpty(kb.id));

      // Rebuild indices: remove the deleted index, shift down those above it
      const newIndices = new Set<number>();
      for (const i of expandedKnowledgeBaseIndices.value) {
        if (i === removeIndex) continue;
        newIndices.add(i > removeIndex ? i - 1 : i);
      }
      expandedKnowledgeBaseIndices.value = newIndices;
    },
  });
};

const handleDeleteKnowledgeBase = async (knowledgeBaseId) => {
  const canProceed = await handleDiscardEditingKnowledgeBase();

  if (canProceed && !isEmpty(knowledgeBaseId)) {
    // Delete existing knowledge base
    deleteKnowledgeBases([knowledgeBaseId]);
  }
};

const handleDeleteKnowledgeBases = async () => {
  const canProceed = await handleDiscardEditingKnowledgeBase();

  if (canProceed && selectedKnowledgeBasesCount.value > 0) {
    deleteKnowledgeBases(getSelectedKnowledgeBaseIds(true));
  }
};

const deleteKnowledgeBases = (knowledgeBaseIds, options?: { title?: string; message?: string }) => {
  modal.show(AuthoringCoPilotKnowledgeBaseDeleteModal, {
    ids: knowledgeBaseIds.filter((id) => !isEmpty(id)),
    title: options?.title,
    message: options?.message,
    actionPrimary: () => {
      if (editingKnowledgeBase.value && knowledgeBaseIds.includes(editingKnowledgeBase.value.id)) {
        // Knowledge base that is currently being edited has been deleted
        editingKnowledgeBase.value = null;
        handleRemoveDirty();
      }

      // Determine which indices are being removed
      const removedIndices = new Set(
        knowledgeBaseIds
          .map((id) => knowledgeBases.value.findIndex((kb) => kb.id === id))
          .filter((i) => i !== -1),
      );

      // Remove deleted knowledge base(s)
      knowledgeBases.value = knowledgeBases.value.filter((kb) => !knowledgeBaseIds.includes(kb.id));
      selectedKnowledgeBases.value = selectedKnowledgeBases.value.filter(
        (kb) => !knowledgeBaseIds.includes(kb.id),
      );

      // Rebuild expanded indices after removal
      const newIndices = new Set<number>();
      for (const i of expandedKnowledgeBaseIndices.value) {
        if (removedIndices.has(i)) continue;
        const shift = [...removedIndices].filter((r) => r < i).length;
        newIndices.add(i - shift);
      }
      expandedKnowledgeBaseIndices.value = newIndices;
    },
  });
};

const validate = (): boolean => {
  if (editingKnowledgeBase.value.contentType !== KnowledgeBaseContentType.TEXT) {
    return true;
  }
  // Validate content input field
  const targetEditor = editorRef.value;
  const isContentValid = targetEditor[0].validate();

  return isContentValid;
};

const populateKnowledgeBaseRequest = () => {
  const { id, indexNo, contentType, contentId, content } = editingKnowledgeBase.value;

  const requestData: Record<string, unknown> = {
    resourceType: props.resourceType,
    resourceId: props.resourceId,
    id,
    indexNo,
    contentType,
  };

  switch (contentType) {
    case KnowledgeBaseContentType.TEXT:
      requestData.rt_content = content;
      break;
    case KnowledgeBaseContentType.MEDIA:
    case KnowledgeBaseContentType.GOOGLE:
      requestData.contentId = contentId;
      if (content) requestData.content = content;
      break;
  }

  return requestData;
};

const resetEditingAndExpandedStates = async () => {
  const isDiscarded = await handleDiscardEditingKnowledgeBase();

  if (isDiscarded) {
    expandedKnowledgeBaseIndices.value = new Set();
  }

  return isDiscarded;
};

const resetProcessingState = () => {
  saveButton.value.disabled = false;
  saveButton.value.loading = false;
  saveButton.value.error = false;
  saveButton.value.errorMessage = "";
};

const setProcessingError = () => {
  saveButton.value.loading = false;
  saveButton.value.error = true;
};

const initSelection = () => {
  if (props.isAllKnowledgeBaseSelected) {
    selectedKnowledgeBases.value = [...knowledgeBases.value];
  } else {
    const selectedKnowledgeBaseObjects = knowledgeBases.value.filter((item) =>
      props.selectedKnowledgeBaseIds.includes(item.id),
    );
    selectedKnowledgeBases.value = [...selectedKnowledgeBaseObjects];
  }
};

const validateSelectedKnowledgeBase = () => {
  // Expose this method for parent components to validate
  return selectedKnowledgeBases.value.length > 0;
};

const handleOpenPdfInNewTab = (uuid: string) => {
  const routeData = router.resolve({ name: "PdfView", params: { uuid } });
  window.open(routeData.href, "_blank");
};

const handleMediaUnapprovedErrorChanged = (itemId: number, hasError: boolean): void => {
  mediaUnapprovedErrorMap.value.set(itemId, hasError);
};

// Watch
watch(
  selectedKnowledgeBases,
  (newVal) => {
    emit("knowledge-base-selection", {
      selectedKnowledgeBases: newVal,
      knowledgeBase: knowledgeBases.value,
    });
  },
  { deep: true },
);

onMounted(async () => {
  await fetchKnowledgeBases();
  mediaEventBus.$on("updateMediaObject", handleUpdateMediaObject);
  isLoading.value = false;
});

onBeforeUnmount(() => {
  handleRemoveDirty();
});

defineExpose({
  selectedKnowledgeBases,
  resetEditingAndExpandedStates,
  validateSelectedKnowledgeBase,
});
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.authoring-copilot-knowledge-base {
  .header {
    .title {
      @include carbon--type-style("productive-heading-01", true);
      margin-bottom: $spacing-02;
    }

    .info {
      @include carbon--type-style("body-short-01", true);
      margin-bottom: $spacing-06;
      color: $text-02;
      white-space: pre-line;
    }
  }

  .knowledge-base-listing {
    .bx--accordion {
      position: relative;

      &.without-checkbox {
        .bx--accordion__heading {
          padding-left: $spacing-05;
        }
      }

      &:has(.bx--overflow-menu) {
        .bx--accordion__title {
          &::after {
            right: 3.2rem;
          }
        }
      }

      &__heading {
        min-height: 48px;
        align-items: center;
        padding: $spacing-04 0 $spacing-04 $spacing-09;

        .bx--accordion__title {
          margin-left: $spacing-01;
          padding-right: $spacing-11;

          &::after {
            top: 3px;
          }

          .title {
            @include carbon--type-style("productive-heading-01", true);
            @include line-clamp(1);
          }

          .icon {
            position: absolute;
            top: 0;
            right: 5rem;
          }
        }
      }

      &__item {
        margin-bottom: $spacing-05;

        & ~ .message-component.error {
          margin: -0.75rem 0 $spacing-05;
        }

        &.is-edit-mode {
          .bx--accordion__heading {
            .bx--accordion__title {
              .icon {
                display: none;
              }
            }
          }

          .bx--accordion__content {
            padding: 0 $spacing-05 $spacing-03;
          }

          .media-component {
            .star-resource-button {
              display: none;
            }
          }
        }
      }

      &__item--active {
        .bx--accordion__heading {
          .bx--accordion__title {
            &::after {
              top: 9px;
            }
          }
        }

        & ~ .menu-actions {
          .bx--overflow-menu__trigger {
            .sls-icon {
              [fill] {
                fill: $inverse-link-01;
              }
            }
          }
        }

        &.is-edit-mode {
          .bx--accordion__heading {
            .bx--accordion__title {
              &::after {
                right: 1.2rem;
              }

              .icon {
                display: inline-block;
                right: 2.75rem;
              }
            }
          }

          & ~ .menu-actions {
            display: none;
          }
        }
      }

      > .input-checkbox {
        position: absolute;
        top: 0.7rem;
        left: 1rem;
        margin: 0;
      }

      > .menu-actions {
        position: absolute;
        top: 0.25rem;
        right: 0.3rem;
        z-index: z-map("default");
      }

      &__content {
        padding: $spacing-05;

        .bx--inline-notification {
          &:first-child {
            margin-top: 0;
          }
        }
      }

      .media {
        // remove margin to keep accordion content spacing consistent with media component
        // as kb accordion only has 1 media component or text so top and bottom margin is not needed to separate medias or text
        margin: 0;

        &.no-preview.pdf {
          .attachment-placeholder {
            // added border to align icons in line with other
            // type of media that has border on figure.media
            border: 1px solid $ui-02;
          }
        }
      }

      .attachment-placeholder {
        background-color: $ui-background;
      }
    }
  }

  .form-knowledge-base {
    .knowledge-base-content {
      .field-input {
        @include carbon--type-style("body-short-01", true);
      }
    }

    .actions {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: $spacing-05;
      margin-top: $spacing-06;
    }
  }

  .add-knowledge-base {
    margin: 0;
  }
}
</style>
